import { useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageLayout } from '@/components/shared/PageLayout';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card/Card';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import type { Tool } from '@/features/traceability/type/Tool';
import type { ReferenceComparisonAggregation } from '@/features/analytics/types';
import { buildReferenceComparisonAggregations } from '../../utils/comparisonAggregations';
import { filterTools } from '../../utils/filterTraces';

import { ComplexCharts } from '../shared/ComplexCharts';
import { ReferenceSelector } from './ReferenceSelector';
import { FilterControls } from './FilterControls';
import { MetricsSection } from './MetricsSection';
import { ReferencePerformanceCharts } from './ReferencePerformanceCharts';
import { ToolsReferencesCharts } from './ToolsReferencesCharts';

type ReferenceComparisonLayoutProps = {
  tools: Tool[];
  comparisonOptions: string[];
  selectedReferences: string[];
  selectedMachine: string;
  selectedMaterial: string;
  selectedOperation: string;
  machineOptions: string[];
  materialOptions: string[];
  operationOptions: string[];
  isLoading: boolean;
  isRefreshing: boolean;
  referenceContext?: string; // Optional reference from URL parameter
  onPrimarySelect: (value: string | null) => void;
  onToggleReference: (reference: string) => void;
  onMachineChange: (value: string) => void;
  onMaterialChange: (value: string) => void;
  onOperationChange: (value: string) => void;
  onClearFilters: () => void;
  onRefresh: () => void;
  onNavigateBack: () => void;
  onNavigateHome: () => void;
};

export function ReferenceComparisonLayout({
  tools,
  comparisonOptions,
  selectedReferences,
  selectedMachine,
  selectedMaterial,
  selectedOperation,
  machineOptions,
  materialOptions,
  operationOptions,
  isLoading,
  isRefreshing,
  referenceContext,
  onPrimarySelect,
  onToggleReference,
  onMachineChange,
  onMaterialChange,
  onOperationChange,
  onClearFilters,
  onRefresh,
  onNavigateBack,
  onNavigateHome,
}: ReferenceComparisonLayoutProps) {
  const { t } = useTranslation();
  const { resolve } = useReferenceAlias();

  // Filter tools based on selected filters
  const filteredTools = useMemo(() => {
    // First, filter by machine/material/operation
    const baseFiltered = filterTools(tools, {
      machine: selectedMachine,
      material: selectedMaterial,
      operation: selectedOperation,
    });
    // Then, if in reference comparison context, filter by selectedReferences
    if (selectedReferences && selectedReferences.length > 0) {
      return baseFiltered.filter(
        (tool) => tool.reference && selectedReferences.includes(tool.reference)
      );
    }
    return baseFiltered;
  }, [tools, selectedMachine, selectedMaterial, selectedOperation, selectedReferences]);

  // Build aggregation
  const aggregation = useMemo<ReferenceComparisonAggregation>(() => {
    if (!filteredTools.length || !selectedReferences.length) {
      return {
        items: [],
        totals: {
          references: 0,
          traces: 0,
          pieces: 0,
        },
      };
    }

    return buildReferenceComparisonAggregations({
      tools: filteredTools,
      references: selectedReferences,
    });
  }, [filteredTools, selectedReferences]);

  const isRefreshDisabled = isRefreshing || isLoading;

  const breadcrumbItems = [
    {
      label: t('common.brand'),
      onClick: onNavigateHome,
    },
    {
      label: t('analytics.comparison.breadcrumb.analytics'),
      onClick: onNavigateBack,
    },
    ...(referenceContext
      ? [
          {
            label: resolve(referenceContext).label,
          },
        ]
      : []),
    {
      label: t('analytics.comparison.breadcrumb.comparison'),
    },
  ];

  return (
    <PageLayout>
      <div className="flex flex-col gap-6">
        <PageHeader
          title={t('analytics.comparison.layout.title')}
          description={t('analytics.comparison.layout.subtitle')}
          breadcrumb={breadcrumbItems}
          actions={
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={onRefresh}
              loading={isRefreshing}
              disabled={isRefreshDisabled}
            >
              {!isRefreshing && <RefreshCw aria-hidden />}
              {t('analytics.comparison.layout.controls.refresh')}
            </Button>
          }
        />

        {/* Reference Selector */}
        <ReferenceSelector
          comparisonOptions={comparisonOptions}
          selectedBaseReference={selectedReferences[0] ?? null}
          selectedComparisonReferences={selectedReferences.slice(1)}
          isLoading={isLoading}
          onBaseReferenceChange={onPrimarySelect}
          onToggleComparisonReference={onToggleReference}
        />

        {/* Filter Controls */}
        {!isLoading && comparisonOptions.length > 0 && (
          <FilterControls
            machineOptions={machineOptions}
            materialOptions={materialOptions}
            operationOptions={operationOptions}
            selectedMachine={selectedMachine}
            selectedMaterial={selectedMaterial}
            selectedOperation={selectedOperation}
            onMachineChange={onMachineChange}
            onMaterialChange={onMaterialChange}
            onOperationChange={onOperationChange}
            onClearFilters={onClearFilters}
          />
        )}

        {/* Metrics Section */}
        <MetricsSection aggregation={aggregation} isLoading={isLoading} />

        {/* Reference Performance Charts */}
        {selectedReferences.length > 0 && (
          <ReferencePerformanceCharts
            tools={filteredTools}
            selectedReferences={selectedReferences}
            isLoading={isLoading}
          />
        )}

        {/* Tools References Distribution Charts */}
        {selectedReferences.length > 0 && (
          <ToolsReferencesCharts
            tools={filteredTools}
            selectedReferences={selectedReferences}
            isLoading={isLoading}
          />
        )}

        {/* Charts Grid */}
        <section className="grid gap-6 xl:grid-cols-2"></section>

        {/* Complex Charts Section */}
        {selectedReferences.length > 0 && (
          <ComplexCharts tools={filteredTools} isLoading={isLoading} mode="comparison" />
        )}

        {/* Bottom Summary Section */}
        <Card as="section" padding="lg">
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-5 w-64" />
              <Skeleton className="h-3 w-80" />
            </div>
          ) : aggregation.items.length > 0 ? (
            <>
              <h2 className="section-title">{t('analytics.comparison.layout.summary.title')}</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {t('analytics.comparison.layout.summary.description', {
                  count: aggregation.items.length,
                  totalTraces: aggregation.totals.traces.toLocaleString(),
                  totalPieces: aggregation.totals.pieces.toLocaleString(),
                })}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('analytics.comparison.layout.state.empty')}
            </p>
          )}
        </Card>
      </div>
    </PageLayout>
  );
}
